import React, {useMemo, useState, useEffect, useCallback, useRef} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useStories} from "../hooks/useStories";
import FilterBar from "../components/FilterBar";
import {getNeuronItemId} from "../utils/getNeuronItemId";
import {trackNeuronEvent} from "../utils/trackNeuronEvent";
import {useViewInterceptor} from "../hooks/useViewInterceptor";

// Reuse your hover tracking constants
const HOVER_EVENT_ID = 1743190718951;
const HOVER_EVENT_TYPE = "hover";
const HOVER_DELAY_MS = 2000;

/**
 * Copy of HoverTrackedTile (kept local to avoid exporting from HomePage)
 * If you prefer, move this into /components/HoverTrackedTile.jsx and reuse everywhere.
 */
const HoverTrackedTile = React.forwardRef(
  ({story, userEmail, onClick, className, children}, ref) => {
    const hoverTimerRef = useRef(null);
    const hasLoggedThisHoverRef = useRef(false);

    const clearHoverTimer = useCallback(() => {
      if (hoverTimerRef.current) {
        window.clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = null;
      }
    }, []);

    useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

    const onHoverStart = useCallback(() => {
      if (!story || !userEmail) return;
      if (hasLoggedThisHoverRef.current) return;

      clearHoverTimer();

      hoverTimerRef.current = window.setTimeout(async () => {
        const neuronItemId = getNeuronItemId(story);
        if (!neuronItemId) return;

        try {
          await trackNeuronEvent({
            itemId: neuronItemId,
            userId: userEmail,
            eventId: HOVER_EVENT_ID,
            type: HOVER_EVENT_TYPE,
          });

          hasLoggedThisHoverRef.current = true;
          window.dispatchEvent(
            new CustomEvent("neuronEventLogged", {
              detail: {message: "Hover event logged.", status: "success"},
            })
          );
        } catch (err) {
          console.error("💥 Error sending hover event:", err);
          window.dispatchEvent(
            new CustomEvent("neuronEventLogged", {
              detail: {message: "Failed to log hover.", status: "error"},
            })
          );
        } finally {
          hoverTimerRef.current = null;
        }
      }, HOVER_DELAY_MS);
    }, [story, userEmail, clearHoverTimer]);

    const onHoverEnd = useCallback(() => {
      clearHoverTimer();
      hasLoggedThisHoverRef.current = false;
    }, [clearHoverTimer]);

    return (
      <div
        ref={ref}
        className={className}
        onMouseEnter={onHoverStart}
        onMouseLeave={onHoverEnd}
        onFocus={onHoverStart}
        onBlur={onHoverEnd}
        onClick={onClick}
        role="button"
        tabIndex={0}
      >
        {children}
      </div>
    );
  }
);

HoverTrackedTile.displayName = "HoverTrackedTile";

const PAGE_SIZE = 24;

const SORT_OPTIONS = [
  {value: "newest", label: "Newest first"},
  {value: "oldest", label: "Oldest first"},
  {value: "title_az", label: "Title A → Z"},
  {value: "title_za", label: "Title Z → A"},
];

const AllStoriesPage = ({userEmail}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-backed state (so reload/share keeps filters)
  const initialCategory = searchParams.get("category") || "";
  const initialQ = searchParams.get("q") || "";
  const initialSort = searchParams.get("sort") || "newest"; // ✅ NEW

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [query, setQuery] = useState(initialQ);
  const [sort, setSort] = useState(initialSort); // ✅ NEW
  const [page, setPage] = useState(1);

  const {stories, loading} = useStories();
  const viewInterceptor = useViewInterceptor({userId: userEmail});

  const categories = useMemo(() => {
    return [
      ...new Set(
        (stories || []).map((s) => s.aiCategorization).filter(Boolean)
      ),
    ];
  }, [stories]);

  const getTimestamp = useCallback((story) => {
    const t = story?.createdDate ? new Date(story.createdDate).getTime() : 0;
    return Number.isNaN(t) ? 0 : t;
  }, []);

  const getTitle = useCallback((story) => {
    return String(story?.originalTitle || story?.aiTitle || story?.title || "")
      .trim()
      .toLowerCase();
  }, []);

  // Keep URL in sync
  useEffect(() => {
    const next = new URLSearchParams(searchParams);

    if (selectedCategory) next.set("category", selectedCategory);
    else next.delete("category");

    if (query) next.set("q", query);
    else next.delete("q");

    if (sort && sort !== "newest")
      next.set("sort", sort); // ✅ NEW (store only if not default)
    else next.delete("sort");

    // reset paging when filters change
    next.delete("page");

    setSearchParams(next, {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, query, sort]);

  const filteredAndSorted = useMemo(() => {
    const list = stories || [];

    const filtered = list.filter((s) => {
      const catOk = selectedCategory
        ? s.aiCategorization === selectedCategory
        : true;

      const q = query.trim().toLowerCase();
      const text = `${s.originalTitle || ""} ${s.title || ""} ${
        s.aiSummary || ""
      }`.toLowerCase();
      const qOk = q ? text.includes(q) : true;

      return catOk && qOk;
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (sort) {
        case "oldest":
          return getTimestamp(a) - getTimestamp(b);
        case "title_az":
          return getTitle(a).localeCompare(getTitle(b));
        case "title_za":
          return getTitle(b).localeCompare(getTitle(a));
        case "newest":
        default:
          return getTimestamp(b) - getTimestamp(a);
      }
    });

    return sorted;
  }, [stories, selectedCategory, query, sort, getTimestamp, getTitle]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSorted.length / PAGE_SIZE)
  );
  const safePage = Math.min(page, totalPages);

  const visible = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredAndSorted.slice(start, start + PAGE_SIZE);
  }, [filteredAndSorted, safePage]);

  const onClickStory = (storyId) => navigate(`/article/${storyId}`);

  return (
    <div ref={viewInterceptor.rootRef} className="px-4 py-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight truncate">
            All stories
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {loading
              ? "Loading…"
              : `${filteredAndSorted.length} story${
                  filteredAndSorted.length === 1 ? "" : "ies"
                }`}
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/")}
          className="shrink-0 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-900"
        >
          Back
        </button>
      </div>

      <div className="mb-4">
        <FilterBar
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={(c) => {
            setPage(1);
            setSelectedCategory(c);
          }}
        />
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input
          value={query}
          onChange={(e) => {
            setPage(1);
            setQuery(e.target.value);
          }}
          placeholder="Search title or summary…"
          className="w-full sm:max-w-md px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-black"
        />

        {/* ✅ NEW: Sort dropdown */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Sort</span>
          <select
            value={sort}
            onChange={(e) => {
              setPage(1);
              setSort(e.target.value);
            }}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-black text-sm"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400">
          Page {safePage} / {totalPages}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(9)].map((_, i) => (
            <div
              key={i}
              className="h-80 rounded-sm bg-gray-200 dark:bg-gray-800 animate-pulse"
            />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="py-20 text-center text-gray-500">
          No stories match your filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((story) => (
            <HoverTrackedTile
              key={story.uuid}
              story={story}
              userEmail={userEmail}
              onClick={() => onClickStory(story.uuid)}
              ref={viewInterceptor.getItemRef(getNeuronItemId(story))}
              className="group hover:cursor-pointer h-80 bg-stone-900 rounded-sm relative overflow-hidden"
            >
              <img
                src={story.image}
                alt={story.title}
                className="w-full h-full object-cover rounded-sm"
                loading="lazy"
              />

              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white">
                <h3 className="text-xl font-bold mb-2 line-clamp-2">
                  {story.originalTitle}
                </h3>
                <p className="text-gray-200 text-sm line-clamp-3">
                  {story.aiSummary}
                </p>
              </div>
            </HoverTrackedTile>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredAndSorted.length > 0 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50"
          >
            Prev
          </button>

          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages}
            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AllStoriesPage;
